import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {NgForm} from "@angular/forms";
import {BehaviorSubject} from "rxjs";
import {finalize, first} from "rxjs/operators";
import {FloatingButtonAction, TypeAction} from "@share/component/floating-button/floating-button";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {TypeCodeErreur} from "@domain/common/http/result";
import {MessageNotif} from "@domain/notification/message-notif";
import {NotificationMessagesService} from "../notification-messages.service";
import {PageHeaderItem} from '@share/component/page-header/page-header';
import {TypeMessageNotif} from "@domain/notification/type-message-notif";
import {TypePushNotification} from "@domain/notification/type-push-notification";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {BalisesPopupComponent} from "../balises/balises-popup.component";

/**
 * Enumération des onglets
 */
export enum Onglets {
    GENERALITES = 'Généralités'
}

/**
 * Détail d'un message de notification (ajout / modification)
 *
 * @author Yannick Gauthier
 * @date 10/03/2026
 */
@Component({
    host: {'data-test-id': 'message-notif-detail'},
    selector: 'message-notif-detail',
    templateUrl: './message-notif-detail.component.html'
})
export class MessageNotifDetailComponent implements OnInit {

    /** Formulaire */
    @ViewChild('form') form: NgForm;

    /** Message en cours */
    message: MessageNotif;

    /** Indique si on est en création */
    isCreation: boolean;

    /** Indicateur d'enregistrement en cours */
    isSaving: boolean = false;

    /** Expose l'enum TypeMessageNotif au template HTML */
    TypeMessageNotif = TypeMessageNotif;

    /** Liste des actions possibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Options du type de message */
    listeTypesMessage: Array<{id: TypeMessageNotif, libelle: string}> = [
        {id: TypeMessageNotif.EMAIL, libelle: this.translateService.instant("admin.bibliotheque.notifications.message.type.email")},
        {id: TypeMessageNotif.CONFIRMATION, libelle: this.translateService.instant("admin.bibliotheque.notifications.message.type.confirmation")}
    ];

    /** Configuration tinymce */
    public tinymceConfigCorps: any;

    /** Options push notification */
    listeTypesPushNotification: Array<{id: TypePushNotification, libelle: string}> = [
        {id: TypePushNotification.NON_DEFINI, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.nonDefini')},
        {id: TypePushNotification.VALIDATION_NDF, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.validationNdf')},
        {id: TypePushNotification.REJET_NDF, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.rejetNdf')},
        {id: TypePushNotification.A_TRAITER_NDF, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.aTraiterNdf')},
        {id: TypePushNotification.REJET_DEPENSE, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.rejetDepense')},
        {id: TypePushNotification.VALIDATION_OD, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.validationOd')},
        {id: TypePushNotification.REJET_OD, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.rejetOd')},
        {id: TypePushNotification.A_TRAITER_OD, libelle: this.translateService.instant('admin.bibliotheque.notifications.message.push.aTraiterOd')}
    ];

    /** Onglets */
    listeTabItems: Array<PageHeaderItem>;
    selectedItem: PageHeaderItem;
    Onglets = Onglets;

    /**
     * Constructeur
     *
     * @param translateService Service de traduction
     * @param router Router
     * @param route Route activée
     * @param notificationMessagesService Service messages
     * @param toastrService Service toast
     * @param confirmService Service confirmation
     */
    constructor(private translateService: TranslateService,
                private router: Router,
                private route: ActivatedRoute,
                private notificationMessagesService: NotificationMessagesService,
                private toastrService: ToastrService,
                private confirmService: ConfirmService,
                private matDialog: MatDialog) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {

        this.route.params.pipe(first()).subscribe(params => {
            const idMessage = params['idMessage'];
            this.isCreation = idMessage === '0';

            //Si en mode création
            if (this.isCreation) {
                //Initialisation du message
                this.message = {
                    idMessage: 0,
                    titre: '',
                    sujet: '',
                    type: TypeMessageNotif.EMAIL,
                    use: false,
                    corps: '',
                    globalise: false,
                    typePushNotification: TypePushNotification.NON_DEFINI,
                    getKey: () => 0
                };

                //Affichage des boutons
                this.defineListeActionsPossibles();
            } else {
                //Chargement du message
                this.loadMessage(Number(idMessage));
            }
        });

        this.listeTabItems = [{
            code: Onglets.GENERALITES,
            libelle: this.translateService.instant('admin.bibliotheque.notifications.message.detail.generalites')
        }];

        this.selectedItem = this.listeTabItems[0];

        //Configuration tinymce, bouton balise
        this.tinymceConfigCorps = {
            toolbar1: (this.getToolbar1AvecBalises()),
            setup: (editor: any) => {
                //Bouton TinyMCE v6+
                if (editor?.ui?.registry?.addButton) {
                    editor.ui.registry.addButton('addbalise', {
                        icon: 'sourcecode',
                        tooltip: this.translateService.instant('admin.bibliotheque.notifications.message.detail.ajouterBalise'),
                        onAction: () => {
                            this.onClickAjouterBalise(editor);
                        }
                    });
                }
            }
        };
    }

    /**
     * Chargement d'un message
     *
     * @param idMessage id du message
     */
    private loadMessage(idMessage: number): void {
        //Chargement d'un message
        this.notificationMessagesService.getMessageById(idMessage)
            .pipe(first())
            .subscribe(result => {
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.message = result?.data?.message;

                    //Sécurisation des champs optionnels
                    if (!this.message.type) {
                        this.message.type = TypeMessageNotif.EMAIL;
                    }
                    if (this.message.type === TypeMessageNotif.CONFIRMATION) {
                        this.message.globalise = false;
                        this.message.sujet = '';
                        this.message.typePushNotification = TypePushNotification.NON_DEFINI;
                    } else {
                        if (this.message.typePushNotification == null) {
                            this.message.typePushNotification = TypePushNotification.NON_DEFINI;
                        }
                    }

                    //Affichage des boutons
                    this.defineListeActionsPossibles();
                } else {
                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                }
            });
    }

    /**
     * Définition des actions possibles
     */
    private defineListeActionsPossibles(): void {
        //Définit les actions affichées dans la barre de boutons flottants (Enregistrer / Supprimer)
        const liste: Array<FloatingButtonAction> = [{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.enregistrer',
            doAction: () => this.saveMessage(),
            isDisabled: () => {
                //On bloque si le formulaire n’est pas valide
                if (!this.form?.valid) {
                    return true;
                }

                //Si en mode message et que le corps est vide, on désactive le bouton de sauvegarde
                if (this.message?.type === TypeMessageNotif.EMAIL && this.isCorpsVideMessage()) {
                    return true;
                }

                //Sinon l’utilisateur peut enregistrer
                return false;
            }
        }];

        //En création, on n’affiche pas l’action de suppression
        if (!this.isCreation) {
            liste.push({
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                doAction: () => this.deleteMessage()
            });
        }

        //Publie la liste pour rafraîchir l’UI
        this.listeActions.next(liste);
    }

    /**
     * Enregistrer
     */
    saveMessage(): void {
        if (!this.message) {
            return;
        }

        //Pour les messages de confirmation, vider les champs non nécessaire
        if (this.message.type === TypeMessageNotif.CONFIRMATION) {
            this.message.sujet = '';
            this.message.globalise = false;
            this.message.typePushNotification = TypePushNotification.NON_DEFINI;
        }

        //Sauvegarde en cours
        this.isSaving = true;

        //Sauvegarde du message
        this.notificationMessagesService.saveMessage(this.message)
            .pipe(first(), finalize(() => this.isSaving = false))
            .subscribe(result => {
                //Si la sauvegarde est effectuée
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    //Toast de confirmation
                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

                    //Si en mode création
                    if (this.isCreation) {
                        //Récupère l’ID créé
                        const idCree = (result as any)?.data?.id_message;
                        if (idCree) {
                            //On passe en mode modification
                            this.isCreation = false;
                            this.message.idMessage = Number(idCree);

                            //Recalcule les actions (ajout du bouton Supprimer)
                            this.defineListeActionsPossibles();

                            //Navigation vers la route de détail avec l’ID nouvellement créé
                            this.router.navigate(['../' + idCree], {relativeTo: this.route});
                        }
                    }
                } else {
                    //Toast d'erreur
                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                }
            });
    }

    /**
     * Suppression
     */
    deleteMessage(): void {
        if (!this.message?.idMessage) {
            return;
        }

        //Confirmation de suppression
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(first())
            .subscribe(isConfirmed => {
                if (!isConfirmed) {
                    return;
                }

                //Suppression du message
                this.notificationMessagesService.deleteMessage(this.message.idMessage)
                    .pipe(first())
                    .subscribe(result => {
                        if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                            this.toastrService.success(this.translateService.instant('global.success.suppression'));
                            this.onGoBack();
                        } else if (result?.codeErreur === 901) {
                            //Notification utilisée dans le paramétrage workflow
                            this.toastrService.error(this.translateService.instant('admin.bibliotheque.notifications.message.erreurs.utiliseDansWorkflow'));
                        } else {
                            TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                        }
                    });
            });
    }

    /**
     * Retour liste
     */
    onGoBack(): void {
        this.router.navigate([`Admin/Bibliotheque/Notification/Notifications`]);
    }

    /**
     * Indique si le corps du message est vide
     */
    private isCorpsVideMessage(): boolean {
        const html = this.message?.corps || '';
        const sansHtml = html.replace(/<[^>]*>/g, '');
        const texte = sansHtml.replace(/&nbsp;/gi, ' ').trim();
        return texte.length === 0;
    }

    /**
     * Retourne la toolbar de tinymce avec le bouton addbalise
     */
    private getToolbar1AvecBalises(): string {
        //On repart de la toolbar par défaut du tinymce-input et on ajoute addbalise à la fin
        return 'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent blockquote | superscript subscript | addbalise';
    }

    /**
     * Gestion du clic sur le bouton addbalise
     *
     * @param editor Instance du tinymce
     */
    private onClickAjouterBalise(editor: any): void {
        this.matDialog.open(BalisesPopupComponent, {
            width: '900px',
            maxWidth: '95vw',
            data: {editor}
        });
    }
}